import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import HubIcon from '@mui/icons-material/HubOutlined';
import BlockIcon from '@mui/icons-material/BlockOutlined';
import LinkIcon from '@mui/icons-material/LinkOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/AddOutlined';
import { useData } from '../store/data';
import { useFilters } from '../store/filters';
import { fmtInt } from '../lib/format';
import {
  aggregateByTeam,
  applyAliases,
  buildResolvedLinks,
  findLinkColumns,
  linkLabel,
  loadAliases,
  saveAliases,
  type AliasMap,
  type LinkClass,
  type TeamMatrixCell,
} from '../lib/dependencies';
import { StatusChip } from './StatusChip';
import { isDone } from '../lib/status';

// Priorität-Buckets: semantische Namen, die der User pflegt und mit
// Projekt-Prefixes verknüpft. Default = leer — die Zahnrad-Config erlaubt
// dem User, eigene Buckets anzulegen.
const PRIORITY_TEAMS_KEY = 'schuecal.depsPriorityTeams';
// Legacy: früherer Default. Wird beim Laden entfernt, damit Bestandsuser
// die Karten nicht mehr sehen (waren sowieso immer 0).
const LEGACY_DEFAULTS = ['Tools', 'Features'];

function loadPriorityTeams(): string[] {
  try {
    const raw = localStorage.getItem(PRIORITY_TEAMS_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr) && arr.every(x => typeof x === 'string')) {
        // Einmalige Bereinigung: alte Default-Einträge entfernen, wenn
        // exakt = Legacy-Default.
        const isLegacyDefault =
          arr.length === LEGACY_DEFAULTS.length &&
          arr.every((v, i) => v === LEGACY_DEFAULTS[i]);
        if (isLegacyDefault) {
          localStorage.setItem(PRIORITY_TEAMS_KEY, JSON.stringify([]));
          return [];
        }
        return arr;
      }
    }
  } catch { /* noop */ }
  return [];
}

function savePriorityTeams(list: string[]): void {
  localStorage.setItem(PRIORITY_TEAMS_KEY, JSON.stringify(list));
}

type Scope = 'open' | 'all';

const CLASS_LABEL: Record<LinkClass, string> = {
  blocker:  'wird blockiert',
  blocking: 'blockiert andere',
  related:  'verwandt',
};

const CLASS_COLOR: Record<LinkClass, 'error' | 'warning' | 'default'> = {
  blocker:  'error',
  blocking: 'warning',
  related:  'default',
};

export function DependencyBreakdown() {
  const rows = useData(s => s.rows);
  const teamFilter = useFilters(s => s.team);
  const [scope, setScope] = useState<Scope>('open');
  const [classFilter, setClassFilter] = useState<LinkClass | 'all'>('blocker');
  const [priorityTeams, setPriorityTeams] = useState<string[]>(() => loadPriorityTeams());
  const [aliases, setAliases] = useState<AliasMap>(() => loadAliases());
  const [cfgOpen, setCfgOpen] = useState(false);

  const linkCols = useMemo(() => findLinkColumns(rows), [rows]);
  const rawLinks = useMemo(
    () => buildResolvedLinks(rows, { cols: linkCols, onlyOpenSources: scope === 'open' }),
    [rows, linkCols, scope]
  );
  const links = useMemo(() => applyAliases(rawLinks, aliases), [rawLinks, aliases]);

  // Team-Filter aus dem globalen Filter oben: wenn gesetzt, nur Source-Team=Filter zeigen
  const filteredLinks = useMemo(() => {
    let ls = links;
    if (teamFilter) ls = ls.filter(l => l.sourceTeam === teamFilter);
    if (classFilter !== 'all') ls = ls.filter(l => l.cls === classFilter);
    return ls;
  }, [links, teamFilter, classFilter]);

  const matrix = useMemo(() => aggregateByTeam(filteredLinks), [filteredLinks]);

  // Alle jemals im Snapshot vorkommenden Ziel-Teams (Snapshot + Fallback-
  // Prefixe) — für das Config-Widget als Auswahlliste.
  const availablePrefixes = useMemo(() => {
    const s = new Set<string>();
    for (const l of rawLinks) s.add(l.targetTeam);
    return Array.from(s).sort();
  }, [rawLinks]);

  // Set aller Team-Namen die tatsächlich als Source im Snapshot vorkommen —
  // für die "andere"-Kategorie im Tooltip.
  const snapshotTeams = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows) {
      const t = (r as Record<string, unknown>)['Team Name'];
      if (typeof t === 'string' && t) s.add(t);
    }
    return s;
  }, [rows]);

  // Zeilen sind Source-Teams (unsere Teams, deren Tickets betroffen sind).
  // Aufteilung nach Ziel-Team im Tooltip: bekannte Snapshot-Teams namentlich,
  // Rest als „andere".
  const sourceTeamsSorted = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const l of filteredLinks) {
      const key = l.sourceTeam || '(kein Team)';
      let s = map.get(key);
      if (!s) { s = new Set(); map.set(key, s); }
      s.add(l.sourceKey);
    }
    return Array.from(map.entries())
      .map(([team, keys]) => ({ team, count: keys.size }))
      .sort((a, b) => b.count - a.count);
  }, [filteredLinks]);

  // Empty-State: keine Link-Spalten im CSV → Feature-Prompt
  if (linkCols.length === 0) {
    return <NoLinksAlert />;
  }

  // KPI-Zeile: pro Priority-Team wieviele Source-Tickets haben mindestens
  // einen Link mit aktueller Klasse zu diesem Ziel-Team?
  const kpiPerTargetTeam = priorityTeams.map(target => {
    const affectedKeys = new Set<string>();
    for (const l of filteredLinks) {
      if (l.targetTeam === target) affectedKeys.add(l.sourceKey);
    }
    return { team: target, count: affectedKeys.size };
  });

  const totalAffected = new Set(filteredLinks.map(l => l.sourceKey)).size;

  return (
    <Stack spacing={2}>
      {/* Kopfleiste mit Optionen */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="caption" color="text.secondary">
            <b>{linkCols.length}</b> Link-Spalten im CSV gefunden. Aktuelle Auswahl: <b>{filteredLinks.length}</b> Links über <b>{totalAffected}</b> Source-Tickets.
            {teamFilter && <> · Source-Team gefiltert: <b>„{teamFilter}"</b>.</>}
          </Typography>
        </Box>

        <ToggleButtonGroup
          size="small"
          value={scope}
          exclusive
          onChange={(_, v) => v && setScope(v as Scope)}
        >
          <ToggleButton value="open">nur offen</ToggleButton>
          <ToggleButton value="all">alle</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          size="small"
          value={classFilter}
          exclusive
          onChange={(_, v) => v && setClassFilter(v as LinkClass | 'all')}
        >
          <ToggleButton value="blocker">
            <BlockIcon fontSize="small" sx={{ mr: 0.5 }} /> wird blockiert
          </ToggleButton>
          <ToggleButton value="blocking">blockiert</ToggleButton>
          <ToggleButton value="related">verwandt</ToggleButton>
          <ToggleButton value="all">alle</ToggleButton>
        </ToggleButtonGroup>

        <Tooltip title="Priorität-Buckets & Alias-Zuordnung konfigurieren">
          <IconButton size="small" onClick={() => setCfgOpen(true)}>
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <ConfigDialog
        open={cfgOpen}
        onClose={() => setCfgOpen(false)}
        priorityTeams={priorityTeams}
        aliases={aliases}
        availablePrefixes={availablePrefixes}
        onSave={(pt, al) => {
          savePriorityTeams(pt);
          saveAliases(al);
          setPriorityTeams(pt);
          setAliases(al);
        }}
      />

      {/* KPI-Karten für Priority-Teams — nur zeigen wenn welche gesetzt sind */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {kpiPerTargetTeam.length === 0 && (
          <Card sx={{ minWidth: 260, flex: 1, borderStyle: 'dashed', borderColor: 'divider' }} variant="outlined">
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="overline" color="text.secondary">
                Priority-Buckets
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Über das <SettingsIcon fontSize="inherit" sx={{ verticalAlign: 'middle', mx: 0.25 }} />-Icon
                oben kannst du semantische Ziel-Gruppen anlegen (z. B. „Tools = @SDO, @DEVO") —
                dann werden hier KPI-Karten dafür angezeigt.
              </Typography>
            </CardContent>
          </Card>
        )}
        {kpiPerTargetTeam.map(k => {
          const aliasPrefixes = aliases[k.team] ?? [];
          const isMappedViaAlias = aliasPrefixes.length > 0;
          const isInSnapshot = availablePrefixes.includes(k.team);
          const isMissing = !isInSnapshot && !isMappedViaAlias;
          return (
            <Card key={k.team} sx={{ minWidth: 200, flex: 1, opacity: isMissing ? 0.6 : 1 }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="overline" color="text.secondary">
                  {classFilter === 'all' ? 'Abhängig zu' : CLASS_LABEL[classFilter as LinkClass]} · {k.team}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtInt(k.count)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Source-Tickets{scope === 'open' ? ' (offen)' : ''}
                </Typography>
                {isMappedViaAlias && (
                  <Chip
                    size="small"
                    label={`Alias: ${aliasPrefixes.join(', ')}`}
                    sx={{ mt: 0.5, height: 18, fontSize: 10 }}
                  />
                )}
                {isMissing && (
                  <Chip
                    size="small"
                    color="warning"
                    label="nicht im Snapshot — Alias setzen"
                    sx={{ mt: 0.5, height: 18, fontSize: 10 }}
                    onClick={() => setCfgOpen(true)}
                  />
                )}
              </CardContent>
            </Card>
          );
        })}
        <Card sx={{ minWidth: 200, flex: 1, bgcolor: 'action.hover' }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="overline" color="text.secondary">
              Σ über alle Ziel-Teams
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
              {fmtInt(totalAffected)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              einzigartige Source-Tickets
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Blockade-Sicht: pro Snapshot-Team → wie viele Tickets betroffen + wer blockiert */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Blockade-Sicht pro Snapshot-Team
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Eine Zeile pro Team aus deinem Snapshot. Zahl = wie viele Tickets aus diesem Team sind aktuell betroffen
            (bei Filter „wird blockiert": von anderen blockiert). Hover über die Klassen-Chips zeigt <b>wer</b> blockiert.
            Ziel-Tickets, die nicht im Snapshot sind, werden als <b>„andere"</b> aggregiert.
          </Typography>
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Source-Team (aus dem Snapshot)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Betroffene Tickets (unique)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Σ Links</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Klassen-Aufteilung · hover: wer blockiert</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sourceTeamsSorted.map(({ team, count }) => (
                  <SourceTeamRow
                    key={team}
                    team={team}
                    count={count}
                    matrix={matrix}
                    priorityTeams={priorityTeams}
                    snapshotTeams={snapshotTeams}
                  />
                ))}
                {sourceTeamsSorted.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}>
                      Keine passenden Links in dieser Auswahl.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Details-Tabelle: Source-Tickets mit ihren Links */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Source-Tickets mit Abhängigkeiten (max. 200 Zeilen)
          </Typography>
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Source Key</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Source Team</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Beziehung</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Ziel-Ticket</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Ziel-Team</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Ziel-Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLinks.slice(0, 200).map((l, i) => (
                  <TableRow key={`${l.sourceKey}-${l.targetKey}-${i}`} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{l.sourceKey}</TableCell>
                    <TableCell>{l.sourceTeam || '–'}</TableCell>
                    <TableCell><StatusChip status={l.sourceStatus} /></TableCell>
                    <TableCell>
                      <Tooltip arrow title={`${l.direction} · ${l.typeName}`}>
                        <Chip
                          size="small"
                          label={linkLabel(l.direction, l.typeName)}
                          color={CLASS_COLOR[l.cls]}
                          variant={l.cls === 'blocker' ? 'filled' : 'outlined'}
                          sx={{ height: 22 }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{l.targetKey}</TableCell>
                    <TableCell sx={{ fontWeight: priorityTeams.includes(l.targetTeam ?? '') ? 700 : 400 }}>
                      {l.targetTeam ?? <em>nicht im Snapshot</em>}
                      {l.targetTeam && !isDone(l.targetStatus ?? '') && ' '}
                    </TableCell>
                    <TableCell>
                      {l.targetStatus ? <StatusChip status={l.targetStatus} /> : <em style={{ color: '#9e9e9e' }}>–</em>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {filteredLinks.length > 200 && (
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
              Anzeige auf die ersten 200 Zeilen begrenzt (Σ {fmtInt(filteredLinks.length)} Links).
              Nutze Team-Filter oben oder Klassen-Filter, um die Liste zu verkleinern.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}

function NoLinksAlert() {
  return (
    <Alert severity="info" icon={<LinkIcon />} sx={{ borderRadius: 3 }}>
      <AlertTitle>Keine Link-Daten im CSV</AlertTitle>
      Die aktuell geladene CSV enthält keine Ticket-Link-Spalten (z.B. <code>Inward issue link (Blocks)</code>).
      Um Abhängigkeiten zu sehen, muss der JIRA-Export die Link-Spalten enthalten.
      <br /><br />
      <b>So kommst du an die Link-Spalten:</b>
      <ul style={{ marginTop: 4, marginBottom: 0 }}>
        <li>JIRA-Suche → Filter → <b>Export</b> → <b>CSV (All fields)</b> statt „Current fields"</li>
        <li>oder in den <em>Export-Optionen</em> die Felder <code>Linked Issues</code> / <code>Issue Links</code> hinzufügen</li>
        <li>JIRA exportiert dann pro Link-Typ Spalten wie <code>Inward issue link (Blocks)</code> und <code>Outward issue link (Blocks)</code></li>
        <li>Danach CSV hier neu importieren — dieses Akkordeon aktiviert sich automatisch</li>
      </ul>
    </Alert>
  );
}

// Icon-Export für die Section-Registrierung
export { HubIcon as DependencyIcon };

function SourceTeamRow({
  team, count, matrix, priorityTeams, snapshotTeams,
}: {
  team: string;
  count: number;
  matrix: TeamMatrixCell[];
  priorityTeams: string[];
  snapshotTeams: Set<string>;
}) {
  const cells = matrix.filter(m => m.sourceTeam === team);
  const totalLinks = cells.reduce((s, c) => s + c.linkCount, 0);
  const isPriority = priorityTeams.includes(team);

  // Chips pro Klasse aggregieren (über alle Ziel-Teams).
  const byClass = new Map<LinkClass, { count: number; perTarget: Map<string, number> }>();
  for (const c of cells) {
    let e = byClass.get(c.cls);
    if (!e) { e = { count: 0, perTarget: new Map() }; byClass.set(c.cls, e); }
    e.count += c.uniqueSourceTickets;
    e.perTarget.set(
      c.targetTeam,
      (e.perTarget.get(c.targetTeam) ?? 0) + c.uniqueSourceTickets,
    );
  }

  return (
    <TableRow hover sx={isPriority ? { bgcolor: 'action.selected' } : undefined}>
      <TableCell sx={{ fontWeight: isPriority ? 700 : 500 }}>
        {team}{isPriority && <Chip size="small" label="priorität" sx={{ ml: 1, height: 18, fontSize: 10 }} />}
      </TableCell>
      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
        {fmtInt(count)}
      </TableCell>
      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
        {fmtInt(totalLinks)}
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {Array.from(byClass.entries()).map(([cls, e]) => {
            // Ziel-Teams aufteilen: bekannte Snapshot-Teams namentlich,
            // alles andere (nicht importierte Teams, @-Prefix, aliased)
            // wird als „andere" zusammengefasst.
            const known: [string, number][] = [];
            let others = 0;
            for (const [tgt, n] of e.perTarget.entries()) {
              if (tgt && snapshotTeams.has(tgt)) known.push([tgt, n]);
              else                                others += n;
            }
            known.sort((a, b) => b[1] - a[1]);
            const lines: string[] = known.map(([tgt, n]) => `${tgt}: ${fmtInt(n)}`);
            if (others > 0) lines.push(`andere: ${fmtInt(others)}`);
            return (
              <Tooltip
                key={cls}
                arrow
                title={
                  <Box sx={{ whiteSpace: 'pre-line', fontSize: 12, maxWidth: 340 }}>
                    <b>Wer blockiert (Ziel-Team):</b>{'\n'}{lines.join('\n')}
                    {others > 0 && (
                      <>
                        {'\n\n'}
                        <i>„andere" = Ziel-Tickets, deren Team nicht im aktuellen Snapshot enthalten ist. Also andere JIRA-Projekte oder Teams, die noch nicht mit importiert wurden (z. B. Tools, Features).</i>
                      </>
                    )}
                  </Box>
                }
              >
                <Chip
                  size="small"
                  color={CLASS_COLOR[cls]}
                  variant={cls === 'blocker' ? 'filled' : 'outlined'}
                  label={`${CLASS_LABEL[cls]}: ${fmtInt(e.count)}`}
                  sx={{ height: 22, cursor: 'help' }}
                />
              </Tooltip>
            );
          })}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

function ConfigDialog({
  open, onClose, priorityTeams, aliases, availablePrefixes, onSave,
}: {
  open: boolean;
  onClose: () => void;
  priorityTeams: string[];
  aliases: AliasMap;
  availablePrefixes: string[];
  onSave: (pt: string[], al: AliasMap) => void;
}) {
  const [pt, setPt]         = useState<string[]>(priorityTeams);
  const [al, setAl]         = useState<AliasMap>(aliases);
  const [newAlias, setNew]  = useState('');

  // Beim Öffnen: aktuellen Stand übernehmen
  useMemo(() => { if (open) { setPt(priorityTeams); setAl(aliases); setNew(''); } }, [open, priorityTeams, aliases]);

  function addPriorityTeam() {
    const v = newAlias.trim();
    if (!v || pt.includes(v)) return;
    setPt([...pt, v]);
    if (!al[v]) setAl({ ...al, [v]: [] });
    setNew('');
  }
  function removePriorityTeam(name: string) {
    setPt(pt.filter(x => x !== name));
    const nextAl = { ...al };
    delete nextAl[name];
    setAl(nextAl);
  }
  function setAliasPrefixes(name: string, prefixes: string[]) {
    setAl({ ...al, [name]: prefixes });
  }

  // Nur Prefix-Fallback-Ziele („@…") als wählbare Prefix-Optionen anbieten
  const prefixOptions = availablePrefixes.filter(p => p.startsWith('@'));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Priorität-Buckets konfigurieren</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Definiere semantische Ziel-Teams (z. B. „Tools", „Features") und ordne
          ihnen JIRA-Projekt-Prefixe zu. Alle Links, deren Ziel-Ticket nicht im
          Snapshot ist, kommen als <b>@PREFIX</b> in der Liste an.
        </Typography>

        <Stack spacing={2}>
          {pt.map(name => {
            const isMissing = !availablePrefixes.includes(name);
            return (
              <Card key={name} variant="outlined">
                <CardContent sx={{ pb: '12px !important' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700 }}>
                      {name}
                    </Typography>
                    {isMissing && (
                      <Chip size="small" color="warning" label="nicht im Snapshot" />
                    )}
                    <Tooltip title="Entfernen">
                      <IconButton size="small" onClick={() => removePriorityTeam(name)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Autocomplete
                    multiple
                    size="small"
                    options={prefixOptions}
                    value={al[name] ?? []}
                    onChange={(_, next) => setAliasPrefixes(name, next as string[])}
                    freeSolo
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="@PREFIX auswählen (z. B. @SDO, @BU)"
                        helperText="Alle Links zu diesen Prefixen zählen als Ziel-Team oben."
                      />
                    )}
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            );
          })}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <TextField
            size="small"
            placeholder="Neuer Bucket-Name"
            value={newAlias}
            onChange={(e) => setNew(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addPriorityTeam(); }}
            sx={{ flex: 1 }}
          />
          <Button startIcon={<AddIcon />} onClick={addPriorityTeam}>
            Hinzufügen
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant="contained"
          onClick={() => { onSave(pt, al); onClose(); }}
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}
